import { Anchor, AnchorProps, ElementProps } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { ReactNode, Ref, forwardRef } from 'react';

import styles from './Link.module.css';

export type LinkVariant = 'primary' | 'secondary' | 'stepper' | 'tertiary';

type LinkBaseProps = Omit<
  AnchorProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'href'
> &
  Omit<ElementProps<'a', keyof AnchorProps>, 'href'> &
  Omit<NextLinkProps, 'as' | 'passHref' | 'legacyBehavior' | 'href'> & {
    /** Visual variant - primary (purple), secondary (dark, purple on hover), stepper (muted, underline on hover), or tertiary (dark, underline on hover, purple on active) */
    variant?: LinkVariant;
    /** Content to render before the link text */
    leftSection?: ReactNode;
    /** Content to render after the link text. When target="_blank", defaults to external link icon */
    rightSection?: ReactNode;
    /** Whether to apply FullStory PII/PHI masking (fs-mask class) */
    isMasked?: boolean;
    /** Whether to truncate overflowing text with an ellipsis */
    shouldTruncate?: boolean;
    /** Ref to the inner label span */
    labelRef?: Ref<HTMLSpanElement>;
    /** Link content */
    children: ReactNode;
  };

type DownloadLinkProps = LinkBaseProps & {
  /**
   * If true, renders as a download link. If a string, uses that value as the suggested filename.
   * Download links require a string href (not UrlObject) and bypass Next.js Link wrapper.
   */
  download: boolean | string;
  /** Link destination - must be a string for download links */
  href: string;
};

type StandardLinkProps = LinkBaseProps & {
  /** Not a download link */
  download?: false | undefined;
  /** Link destination - can be string or UrlObject */
  href: NextLinkProps['href'];
};

export type LinkProps = DownloadLinkProps | StandardLinkProps;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      variant = 'secondary',
      href,
      prefetch,
      download,
      target,
      rel,
      leftSection,
      rightSection,
      isMasked,
      shouldTruncate,
      labelRef,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isExternalLink = target === '_blank';
    const isSamePageAction = String(href).startsWith('#');

    // Ensure noopener noreferrer is included for target="_blank" links
    const safeRel = isExternalLink
      ? [rel, 'noopener', 'noreferrer'].filter(Boolean).join(' ') ||
        'noopener noreferrer'
      : rel;

    // Default external link icon when target="_blank" and no rightSection provided
    const resolvedRightSection =
      rightSection !== undefined ? (
        rightSection
      ) : isExternalLink ? (
        <IconExternalLink size={20} stroke={2} />
      ) : null;

    const rootClassName = [styles.root, isMasked ? 'fs-mask' : null, className]
      .filter(Boolean)
      .join(' ');

    const anchorElement = (
      <Anchor
        ref={ref}
        unstyled
        href={download ? String(href) : undefined}
        download={download || undefined}
        target={target}
        rel={safeRel}
        className={rootClassName}
        data-variant={variant}
        data-truncate={shouldTruncate || undefined}
        {...props}
      >
        {leftSection && <span className={styles.section}>{leftSection}</span>}
        <span className={styles.label} ref={labelRef}>
          {children}
        </span>
        {resolvedRightSection && (
          <span className={styles.section}>{resolvedRightSection}</span>
        )}
      </Anchor>
    );

    if (download) {
      return anchorElement;
    }

    return (
      <NextLink
        href={href}
        prefetch={isSamePageAction ? false : prefetch}
        passHref
        legacyBehavior
      >
        {anchorElement}
      </NextLink>
    );
  }
);

Link.displayName = 'Link';
